import { FC, useState, useEffect, useCallback } from "react"
import {
  Filter,
  Trash2,
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Plus,
  X
} from "lucide-react"
import {
  Brand,
  FollowupFilterRule,
  FollowupFilterType,
  FollowupFiltersMap
} from "../../../../types"
import BrandAutocomplete from "../../brand-autocomplete"
import { PrimaryBtn } from "../../buttons"
import Tabs from "../../tabs"
import followupFiltersApi from "../../../../api/followup-filters"
import { useAppDispatch } from "../../../hooks"
import { pushToast } from "../../../../redux/slices/notifications"

type TabId = "manage" | "viewAll"

interface FilterSection {
  type: FollowupFilterType
  label: string
}

const FILTER_SECTIONS: FilterSection[] = [
  { type: "lead.created", label: "Lead Created" },
  { type: "subscription.created", label: "Subscription Created" },
  { type: "subscription.dashboard_first_visit", label: "Dashboard First Visit" }
]

const FILTER_KEY_OPTIONS: Array<{
  value: FollowupFilterRule["key"]
  label: string
}> = [
  { value: "state", label: "State" },
  { value: "loid", label: "Loan Officer ID" },
  { value: "app_key", label: "App Key" },
  { value: "disable_manual", label: "Disable Manual" }
]

interface BrandFiltersEntry {
  brandId: string
  filters: FollowupFiltersMap
}

interface Props {
  brands: Brand[]
}

const SmsFollowupFilters: FC<Props> = ({ brands }) => {
  const dispatch = useAppDispatch()
  const [activeTab, setActiveTab] = useState<TabId>("manage")
  const [selectedBrandId, setSelectedBrandId] = useState<string>("")
  const [filters, setFilters] = useState<FollowupFiltersMap>({})
  const [expandedSections, setExpandedSections] = useState<
    Set<FollowupFilterType>
  >(new Set())
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState<FollowupFilterType | null>(null)
  const [isDeleting, setIsDeleting] = useState<FollowupFilterType | null>(null)
  const [allBrandFilters, setAllBrandFilters] = useState<BrandFiltersEntry[]>(
    []
  )
  const [isLoadingAll, setIsLoadingAll] = useState(false)

  const tabs = [
    { id: "manage" as TabId, label: "Manage" },
    { id: "viewAll" as TabId, label: "View All" }
  ]

  // Convert pipe-separated values to comma-separated for display
  const pipeToComma = (value: string): string => {
    return value.replace(/\|\|/g, ", ")
  }

  // Convert comma-separated values to pipe-separated for API
  const commaToPipe = (value: string): string => {
    return value
      .split(",")
      .map(s => s.trim())
      .filter(Boolean)
      .join("||")
  }

  const fetchFiltersForBrand = useCallback(
    async (brandId: string) => {
      if (!brandId) return
      setIsLoading(true)
      try {
        const response = await followupFiltersApi.getByBrand(brandId)
        // Convert pipe separators to commas for display
        const displayFilters: FollowupFiltersMap = {}
        for (const [filterType, rules] of Object.entries(
          response.filters || {}
        )) {
          displayFilters[filterType as FollowupFilterType] = rules?.map(
            rule => ({
              ...rule,
              value:
                typeof rule.value === "string"
                  ? pipeToComma(rule.value)
                  : rule.value
            })
          )
        }
        setFilters(displayFilters)
      } catch (error: any) {
        if (error?.response?.status !== 404) {
          dispatch(
            pushToast({
              msg: "Failed to load filters",
              theme: "danger",
              timeout: 5000
            })
          )
        }
        setFilters({})
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch]
  )

  const fetchAllBrandFilters = useCallback(async () => {
    setIsLoadingAll(true)
    try {
      const response = await followupFiltersApi.getAll()
      // Transform { [brandId]: filters } to array format
      const entries: BrandFiltersEntry[] = Object.entries(
        response.filters || {}
      ).map(([brandId, filters]) => ({ brandId, filters }))
      setAllBrandFilters(entries)
    } catch (error: any) {
      dispatch(
        pushToast({
          msg: "Failed to load brand filters",
          theme: "danger",
          timeout: 5000
        })
      )
      setAllBrandFilters([])
    } finally {
      setIsLoadingAll(false)
    }
  }, [dispatch])

  useEffect(() => {
    if (selectedBrandId) {
      fetchFiltersForBrand(selectedBrandId)
    } else {
      setFilters({})
    }
  }, [selectedBrandId, fetchFiltersForBrand])

  useEffect(() => {
    if (activeTab === "viewAll") {
      fetchAllBrandFilters()
    }
  }, [activeTab, fetchAllBrandFilters])

  const toggleSection = (type: FollowupFilterType) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(type)) {
        next.delete(type)
      } else {
        next.add(type)
      }
      return next
    })
  }

  const updateRule = (
    filterType: FollowupFilterType,
    index: number,
    field: "key" | "value",
    newValue: string | boolean
  ) => {
    setFilters(prev => {
      const rules = [...(prev[filterType] || [])]
      rules[index] = { ...rules[index], [field]: newValue }
      return { ...prev, [filterType]: rules }
    })
  }

  const addRule = (filterType: FollowupFilterType) => {
    setFilters(prev => {
      const rules = [...(prev[filterType] || [])]
      rules.push({ key: "state", value: "" })
      return { ...prev, [filterType]: rules }
    })
  }

  const removeRule = (filterType: FollowupFilterType, index: number) => {
    setFilters(prev => {
      const rules = [...(prev[filterType] || [])]
      rules.splice(index, 1)
      return { ...prev, [filterType]: rules }
    })
  }

  const handleSave = async (filterType: FollowupFilterType) => {
    if (!selectedBrandId) return

    const rules = filters[filterType] || []
    // Convert comma separators to pipes for API
    const apiRules = rules.map(rule => ({
      ...rule,
      value:
        typeof rule.value === "string" ? commaToPipe(rule.value) : rule.value
    }))

    setIsSaving(filterType)
    try {
      await followupFiltersApi.upsert({
        brand_id: parseInt(selectedBrandId, 10),
        filter_type: filterType,
        filter: apiRules,
        mode: "replace"
      })
      dispatch(
        pushToast({
          msg: "Filter saved successfully",
          theme: "success",
          timeout: 3000
        })
      )
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save filter"
      dispatch(
        pushToast({
          msg,
          theme: "danger",
          timeout: 5000
        })
      )
    } finally {
      setIsSaving(null)
    }
  }

  const handleDelete = async (filterType: FollowupFilterType) => {
    if (!selectedBrandId) return

    if (
      !window.confirm(
        `Are you sure you want to delete the "${filterType}" filter?`
      )
    ) {
      return
    }

    setIsDeleting(filterType)
    try {
      await followupFiltersApi.delete(selectedBrandId, filterType)
      setFilters(prev => {
        const next = { ...prev }
        delete next[filterType]
        return next
      })
      dispatch(
        pushToast({
          msg: "Filter deleted successfully",
          theme: "success",
          timeout: 3000
        })
      )
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete filter"
      dispatch(
        pushToast({
          msg,
          theme: "danger",
          timeout: 5000
        })
      )
    } finally {
      setIsDeleting(null)
    }
  }

  const handleViewAllClick = (brandId: string) => {
    setSelectedBrandId(brandId)
    setActiveTab("manage")
  }

  const getBrandName = (brandId: string): string => {
    const brand = brands.find(b => b.id === brandId)
    return brand?.name || `Brand ${brandId}`
  }

  const getRuleCount = (filterType: FollowupFilterType): number => {
    return filters[filterType]?.length || 0
  }

  return (
    <div className="bg-white border border-neutral-200 rounded-lg">
      <div className="px-6 py-5 border-b border-neutral-100 bg-neutral-50 rounded-t-lg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
            <Filter className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              SMS Followup Filters
            </h3>
            <p className="text-sm text-gray-500">
              Manage SMS followup filter rules by brand and filter type
            </p>
          </div>
        </div>
      </div>

      <div className="px-6">
        <Tabs tabs={tabs} currentTab={activeTab} onChange={setActiveTab} />
      </div>

      {activeTab === "manage" && (
        <div className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Brand:
            </label>
            <BrandAutocomplete
              selected={selectedBrandId}
              brands={brands}
              direction="vertical"
              onChange={setSelectedBrandId}
            />
          </div>

          {selectedBrandId && (
            <div className="space-y-3">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                </div>
              ) : (
                FILTER_SECTIONS.map(section => (
                  <div
                    key={section.type}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <button
                      onClick={() => toggleSection(section.type)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        {expandedSections.has(section.type) ? (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        )}
                        <span className="font-medium text-gray-700">
                          {section.label}
                        </span>
                        {getRuleCount(section.type) > 0 && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                            {getRuleCount(section.type)} rule
                            {getRuleCount(section.type) !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </button>

                    {expandedSections.has(section.type) && (
                      <div className="p-4 space-y-3 bg-white">
                        {(filters[section.type] || []).map((rule, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <select
                              value={rule.key}
                              onChange={e =>
                                updateRule(
                                  section.type,
                                  index,
                                  "key",
                                  e.target.value as FollowupFilterRule["key"]
                                )
                              }
                              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            >
                              {FILTER_KEY_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>

                            {rule.key === "disable_manual" ? (
                              <select
                                value={String(rule.value)}
                                onChange={e =>
                                  updateRule(
                                    section.type,
                                    index,
                                    "value",
                                    e.target.value === "true"
                                  )
                                }
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              >
                                <option value="true">True</option>
                                <option value="false">False</option>
                              </select>
                            ) : (
                              <input
                                type="text"
                                value={String(rule.value)}
                                onChange={e =>
                                  updateRule(
                                    section.type,
                                    index,
                                    "value",
                                    e.target.value
                                  )
                                }
                                placeholder={
                                  rule.key === "state"
                                    ? "CA, TX, FL"
                                    : rule.key === "loid"
                                    ? "-1 to disable all"
                                    : "Enter value"
                                }
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              />
                            )}

                            <button
                              onClick={() => removeRule(section.type, index)}
                              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove rule"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}

                        <button
                          onClick={() => addRule(section.type)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          Add Rule
                        </button>

                        <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                          <PrimaryBtn
                            onClick={() => handleSave(section.type)}
                            disabled={isSaving === section.type}
                            className="text-sm"
                          >
                            {isSaving === section.type ? "Saving..." : "Save"}
                          </PrimaryBtn>
                          {getRuleCount(section.type) > 0 && (
                            <button
                              onClick={() => handleDelete(section.type)}
                              disabled={isDeleting === section.type}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            >
                              {isDeleting === section.type ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {!selectedBrandId && (
            <div className="flex flex-col items-center justify-center py-8 text-gray-400">
              <Filter className="w-8 h-8 mb-2" />
              <p className="text-sm">Select a brand to manage filters</p>
            </div>
          )}
        </div>
      )}

      {activeTab === "viewAll" && (
        <div className="p-6">
          <div className="flex items-center justify-end mb-4">
            <button
              onClick={fetchAllBrandFilters}
              disabled={isLoadingAll}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh list"
            >
              <RefreshCw
                className={`w-4 h-4 ${isLoadingAll ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
          </div>

          {isLoadingAll ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
            </div>
          ) : allBrandFilters.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-400">
              <Filter className="w-8 h-8 mb-2" />
              <p className="text-sm">No brands have configured filters</p>
            </div>
          ) : (
            <div className="space-y-3">
              {allBrandFilters.map(entry => (
                <div
                  key={entry.brandId}
                  onClick={() => handleViewAllClick(entry.brandId)}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {getBrandName(entry.brandId)}
                      <span className="ml-2 text-xs font-normal text-gray-400">
                        ID: {entry.brandId}
                      </span>
                    </p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {Object.entries(entry.filters || {}).map(
                        ([filterType, rules]) => (
                          <span
                            key={filterType}
                            className="px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 rounded-full"
                          >
                            {FILTER_SECTIONS.find(s => s.type === filterType)
                              ?.label || filterType}
                            <span className="ml-1 text-gray-500">
                              ({(rules as FollowupFilterRule[])?.length || 0})
                            </span>
                          </span>
                        )
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SmsFollowupFilters
